import { Clipboard } from '@angular/cdk/clipboard';
import { Component, ElementRef, HostListener, Inject, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AwsRumService } from 'analytics-lib';
import { WorkflowConfig } from 'classes-lib';
import { IDocumentType } from 'document-type-picker-lib';
import { Namespace, SectionType, TemplateUsage, Workflow } from 'enums-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { IScriptingBoxConfig, ISignatureConfig, IWorkflowConfig } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { EMPTY, map, Observable, Subscription } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import { UtilsService } from 'utils-service-lib';
import { DialogConfirmComponent, IDialogConfirm } from '../../../common-components/dialog-confirm/dialog-confirm.component';
import { IDocumentDestinationService } from '../../../model/interfaces/idocumentdestinationservice';
import { IDestinationsForOutputFileType } from '../../../services/entity-type/entity-type-service.factory';
import { ServiceProviderServiceFactory } from '../../../services/service-providers/service-provider-service.factory';
import { UiService } from '../../../services/ui.service';
import { WorkflowConfigService } from '../../../services/workflow-config.service';
import { SectionFormFactory } from '../../sections/section-form-factory';
import { TriggerAccordianService } from '../actions/trigger-accordion/trigger-accordian.service';
import { TriggerAccordionComponent } from '../actions/trigger-accordion/trigger-accordion.component';
import { AllowedSectionTypes } from '../allowedSections/AllowedSectionTypes';
import { AwesomedocWorkflowConfigService } from './awesomedoc-config-component/awesomedoc-workflow-config-service';
import { DocMergeWorkflowConfigService } from './docmerge-workflow-config/docmerge-workflow-config-service';
import { SectionUtils } from './section-utils';
import { TemplateLinkService } from './template-link.service';

export interface ISharedTemplateFeatures {
  get sectionValidators(): ValidatorFn;
  get dynamicSectionsToInclude(): SectionType[];
  get autoFillSections(): SectionType[];
  get signatureCombinedOutputDefault(): boolean;
}
@Component({
  selector: 'app-workflow-config',
  templateUrl: './workflow-config.component.html',
  styleUrls: ['./workflow-config.component.scss'],
  standalone: false,
})
export class WorkflowConfigComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  validationIconConfiguration: string;
  validationIconSignatureGroup: string;
  validationIconActionsGroup: string;
  tabsJustLoaded = true;
  tooltipText: string;
  actionsTooltipText: string;
  showUserRestrictions: boolean;
  documentDestinationOptionsAllowed = false;
  fileTypeOptionsAllowed = false;
  showTemplateDestinations: boolean;
  templateFilesValid = false;
  subscriptions: Subscription = new Subscription();
  docxFileName: string;
  outputFileNameSectionFormGroup: UntypedFormGroup;
  outputFileTypeSectionFormGroup: UntypedFormGroup;
  signatureOutputFileTypeSectionFormGroup: UntypedFormGroup;
  destinationsForOutputFileType: IDestinationsForOutputFileType[] = [];
  signatureDestinationsForOutputFileType: IDestinationsForOutputFileType[] = [];
  docxTemplateSectionFormGroup: FormGroup;
  htmlTemplateSectionFormGroup: FormGroup;
  editingTemplateName = false;
  newTemplateName = new FormControl('', Validators.required);
  allowedSectionTypes: AllowedSectionTypes = new AllowedSectionTypes();
  namespace: Namespace;
  originalSectionOrder: string[];

  @ViewChild('editTemplateNameInput') editTemplateNameInput: ElementRef;
  protected readonly Workflow = Workflow;
  private documentDestinationService: IDocumentDestinationService;

  private readonly clipboard = inject(Clipboard);
  private readonly templateLinkService = inject(TemplateLinkService);
  private readonly templateDirectUrlFeatureFlagEnabled = inject(FeatureFlagService).enabled(FeatureFlagService.FLAGS.templateDirectUrl);
  private readonly templateDirectUrlDocMergeFeatureFlagEnabled = inject(FeatureFlagService).enabled(
    FeatureFlagService.FLAGS.templateDirectUrlDocMerge
  );

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogRef: MatDialogRef<WorkflowConfigComponent>,
    private matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public workflowConfig: Readonly<IWorkflowConfig>,
    private workflowConfigService: WorkflowConfigService,
    private uiService: UiService,
    private utilsService: UtilsService,
    private awsRumService: AwsRumService,
    private router: Router,
    private identityStorage: IdentityStorage,
    private serviceProviderServiceFactory: ServiceProviderServiceFactory,
    private messageNotificationService: MessageNotificationService,
    private triggerAccordianService: TriggerAccordianService,
    private awesomedocWorkflowConfigService: AwesomedocWorkflowConfigService,
    private docMergeWorkflowConfigService: DocMergeWorkflowConfigService,
  ) {
    this.namespace = this.identityStorage.get().namespace;
    this.documentDestinationService = this.serviceProviderServiceFactory
      .getServiceProviderService(this.namespace)
      .getDocumentDestinationService();
  }

  get signatureGroup(): UntypedFormGroup {
    return this.form.get('signatureConfig') as UntypedFormGroup; // e.g. Docusign - could be others in the future
  }

  get scriptingBoxGroup(): UntypedFormGroup {
    return this.form.get('scriptingBoxConfig') as UntypedFormGroup;
  }

  get configuration(): UntypedFormGroup {
    return this.form.get('configuration') as UntypedFormGroup;
  }

  get actionsGroup(): FormGroup {
    return this.form.get('actionsConfig') as FormGroup;
  }

  get entity(): string {
    return this.configuration.get('entity').value;
  }

  get isNewConfig(): boolean {
    return !this.workflowConfig.id;
  }

  get canCopyDirectLink(): boolean {
    return (
      !this.isNewConfig &&
      !!this.configuration.get('isPublic')?.value &&
      this.templateLinkService.isDirectLinkEnabled() &&
      this.templateDirectUrlFeatureFlagEnabled() &&
      this.isDirectLinkEnabledForWorkflow()
    );
  }

  get allowSave(): boolean {
    // set timeout?
    return this.form.valid && this.form.dirty && this.form.errors === null && !this.uiService.isSaving();
  }

  get sectionsFormArray(): UntypedFormArray {
    return this.configuration.get('sections') as UntypedFormArray;
  }

  get filtersFormArray(): UntypedFormArray {
    return this.configuration.get('filters') as UntypedFormArray;
  }

  get usageTypesControl(): UntypedFormControl {
    return this.configuration.get('usageTypes') as UntypedFormControl;
  }

  get documentDestinationFormArray(): UntypedFormArray {
    return this.configuration.get('documentDestination') as UntypedFormArray;
  }

  get actionsFormArray(): FormArray {
    return this.form.get('actionsConfig')?.get('actions') as FormArray;
  }

  get hasActionsConfigured(): boolean {
    if (!this.actionsFormArray || this.actionsFormArray.length === 0) {
      return false;
    }

    // Check if any action trigger has actual actions configured
    return this.actionsFormArray.controls.some((actionGroup) => {
      const actionsArray = actionGroup.get('properties')?.get('actions');
      return actionsArray && Array.isArray(actionsArray.value) && actionsArray.value.length > 0;
    });
  }

  private get templateService(): ISharedTemplateFeatures {
    switch (this.workflowConfig.workflow) {
      case Workflow.SimpleDocx:
        return this.awesomedocWorkflowConfigService;
      case Workflow.DocumentMerge:
        return this.docMergeWorkflowConfigService;
      default:
        throw new Error('Unknown workflow: ' + this.workflowConfig.workflow);
    }
  }

  ngOnInit(): void {
    const interaction = {
      description: this.isNewConfig ? 'New Template' : 'Clicked on template row in table',
      workflowConfigId: this.workflowConfig?.id,
    };
    this.awsRumService.recordEventInteraction('Opened_template_config', this.constructor.name, this.router.url, interaction);
    this.utilsService.dialogOpen(true);

    const sectionsFormArray = this.formBuilder.array([], this.templateService.sectionValidators);
    const sections = this.workflowConfig ? this.workflowConfig.sections : [];
    sections.sort((a, b) => a.order - b.order);
    sections.forEach((section) => {
      sectionsFormArray.push(SectionFormFactory.build(section.type, section));
    });

    const filtersFormArray = this.formBuilder.array([]);
    const filters = this.workflowConfig?.filters ?? [];
    filters.forEach((filter) => {
      filtersFormArray.push(
        this.formBuilder.group({
          filterId: new UntypedFormControl(filter.filterId || null),
          entityPath: new UntypedFormControl(filter.entityPath),
          filterValues: new UntypedFormControl(filter.filterValues || []),
          operator: new UntypedFormControl(filter.operator || 'ANY_MATCH'),
          filterType: new UntypedFormControl(filter.filterType || 'USER'),
        }),
      );
    });

    const signatureConfig = this.workflowConfig.actionConfigs?.find((actionConfig) => actionConfig.type === 'signature');
    const scriptingBoxConfig = this.workflowConfig.actionConfigs?.find((actionConfig) => actionConfig.type === 'script');

    const actions = this.workflowConfig.actionConfigs?.filter((actionConfig) => actionConfig.type === 'action') ?? [];
    this.triggerAccordianService.addMissingDefaultTriggers(actions);
    const actionsFormArray = this.formBuilder.array([]);
    const namespace = this.identityStorage.get().namespace;
    actions.forEach((actionConfig) => {
      actionsFormArray.push(TriggerAccordionComponent.buildActionFormGroup(actionConfig, namespace));
    });

    const formGroupService = this.serviceProviderServiceFactory.getSignatureProviderService('docusign').getSignatureFormGroupService();
    const signatureRequest = signatureConfig
      ? (signatureConfig.properties as { request: ISignatureConfig }).request
      : undefined;

    this.form = this.formBuilder.group({
      configuration: this.formBuilder.group({
        id: new UntypedFormControl(this.workflowConfig?.id),
        name: new UntypedFormControl(this.workflowConfig?.name, Validators.required),
        isPublic: new UntypedFormControl(this.workflowConfig?.isPublic || false),
        entity: new UntypedFormControl(this.workflowConfig?.entity, Validators.required),
        sections: sectionsFormArray,
        filters: filtersFormArray,
        documentDestination: this.buildDocumentDestinationFormArray(),
        useFileAttachmentAsTemplate: new UntypedFormControl(this.workflowConfig?.useFileAttachmentAsTemplate || false),
        usageTypes: new UntypedFormControl(this.workflowConfig?.usageTypes ?? [TemplateUsage.AwesomeDocs]),
      }),
      signatureConfig: formGroupService.buildForm(signatureRequest, this.templateService.signatureCombinedOutputDefault),
      scriptingBoxConfig: scriptingBoxConfig
        ? this.buildScriptingBoxForm(scriptingBoxConfig.properties as IScriptingBoxConfig)
        : this.buildScriptingBoxForm(),
      actionsConfig: this.formBuilder.group({ actions: actionsFormArray }),
    });

    // TODO: Think about this - we only have 2 tabs at the moment, if we have more this is gonna get ugly!
    this.updateValidationIconConfiguration();
    this.updateValidationIconSignatureGroup();
    this.updateValidationIconActionsGroup();
    this.updateShowUserRestrictions();

    this.initSubscriptions();

    // this must be done AFTER the main form is created
    this.autoFillSections(this.templateService.autoFillSections);

    this.originalSectionOrder = this.getSectionOrderControls();
    console.debug('Setting original section order:', this.originalSectionOrder);

    this.monitorTemplateFileGroup();

    this.documentDestinationOptionsAllowed = this.documentDestinationService.documentDestinationOptionsAllowed ?? false;
    this.fileTypeOptionsAllowed = this.documentDestinationService.fileTypeOptionsAllowed ?? false;

    this.outputFileNameSectionFormGroup = this.findSectionFormGroup(SectionType.OutputFileName);
    this.outputFileTypeSectionFormGroup = this.findSectionFormGroup(SectionType.OutputFileType);
    this.autoFillSignatureSection();

    if (!this.isNewConfig) {
      this.setDestinationsForOutputFileType();
      this.setSignatureDestinationsForOutputFileType();
    }
    this.showTemplateDestinations = this.identityStorage.get().namespace === Namespace.bullhorn;

    this.docxTemplateSectionFormGroup = this.findSectionFormGroup(SectionType.DocxTemplate);
    this.htmlTemplateSectionFormGroup = this.findSectionFormGroup(SectionType.HtmlDocxTemplate);
  }

  saveWorkflowConfig(): void {
    const workflowConfig = this.isNewConfig
      ? new WorkflowConfig(this.workflowConfig.clientNamespace, this.workflowConfig.workflow)
      : WorkflowConfig.clone(this.workflowConfig);

    const formGroupService = this.serviceProviderServiceFactory.getSignatureProviderService('docusign').getSignatureFormGroupService();
    workflowConfig.name = this.configuration.value.name;
    workflowConfig.isPublic = this.configuration.value.isPublic;
    workflowConfig.entity = this.configuration.value.entity;
    workflowConfig.useFileAttachmentAsTemplate = this.configuration.value.useFileAttachmentAsTemplate;
    workflowConfig.usageTypes = this.configuration.value.usageTypes;
    workflowConfig.sections = [];
    workflowConfig.actionConfigs = [
      {
        type: 'signature',
        id: 'signature1',
        properties: {
          request: formGroupService.getFormValue(this.signatureGroup) as ISignatureConfig,
        },
      },
      {
        id: 'script1',
        type: 'script',
        properties: {
          ...this.form.value.scriptingBoxConfig,
        } as IScriptingBoxConfig,
      },
    ];
    workflowConfig.actionConfigs.push(...this.actionsFormArray.value);

    this.documentDestinationFormArray.controls.forEach((fg: UntypedFormGroup, index: number) => {
      workflowConfig.actionConfigs.push({
        id: `documentDestination${index + 1}`,
        type: 'documentDestination',
        properties: {
          ...(fg.value as { entityType: string; paths: string[] }),
        },
      });
    });

    // should use parseForm - make it return undefined to exclude a section
    this.sectionsFormArray.controls.forEach((fg: UntypedFormGroup) => {
      if (fg.value.required) {
        workflowConfig.sections.push(fg.value); // let's hope the server doesn't mind if other form control values are included!
      }
    });

    this.getDynamicSections().forEach((fg: FormGroup, index: number) => {
      if (fg.valid && fg.value.displayName) {
        const sectionData = fg.value;
        sectionData.order = workflowConfig.sections.length + index;
        sectionData.required = true;
        workflowConfig.sections.push(sectionData);
      }
    });

    workflowConfig.filters = this.configuration.value.filters;

    this.workflowConfigService.save(workflowConfig).subscribe({
      error: (err) => {
        this.messageNotificationService.error(`There was a problem saving '${workflowConfig.name}'.`, err);
      },
      complete: () => {
        this.dialogRef.close(true);
        this.messageNotificationService.success(`Successfully saved '${workflowConfig.name}'`);
      },
    });
  }

  delete(): void {
    const dialogDelete: MatDialogRef<DialogConfirmComponent> = this.matDialog.open(DialogConfirmComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      panelClass: this.namespace + '-namespace',
      data: {
        title: `Delete: ${this.workflowConfig.name}`,
        message: 'Are you sure you want to delete this Template?',
        positiveText: 'Yes',
        negativeText: 'No',
      } as IDialogConfirm,
    });

    dialogDelete.afterClosed().subscribe((takeAction: boolean) => {
      if (!takeAction) {
        return;
      }

      this.workflowConfigService.delete(this.workflowConfig).subscribe({
        complete: () => this.dialogRef.close(true),
      });
    });
  }

  copyDirectLink(): void {
    this.clipboard.copy(this.templateLinkService.buildDirectLink(this.workflowConfig));
    this.messageNotificationService.success('Direct link copied to clipboard.');
  }

  discardLocalChanges(e = null): void {
    if (e) {
      e.preventDefault();
    } // prevent submission of parent form.

    if (this.form.dirty) {
      const confirmDialogRef: MatDialogRef<DialogConfirmComponent> = this.matDialog.open(DialogConfirmComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        panelClass: this.namespace + '-namespace',
        data: {
          title: `Changes made`,
          titleIcon: 'info_outline',
          message: this.allowSave ? 'Would you like to save your changes? ' : 'Would you like to go back, amend and save? ',
          positiveText: 'Discard',
          negativeText: 'Go Back',
          alternativeText: this.allowSave ? 'Save' : null,
        } as IDialogConfirm,
      });

      confirmDialogRef.afterClosed().subscribe((takeAction: any) => {
        if (!takeAction) {
          return;
        } else {
          this.dialogRef.close(false);
        }

        if (takeAction === 'Save') {
          this.saveWorkflowConfig();
        }
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  updateDocumentForDestinationType(
    event: { destinationType: string; documentType: IDocumentType },
    formGroup: UntypedFormGroup,
    destinations: IDestinationsForOutputFileType[]
  ): void {
    const sourceValueControl = formGroup.get('sourceValue');
    const documentTypeIsInvalid = !!event.documentType && !event.documentType.id;

    if (!this.documentDestinationOptionsAllowed) {
      sourceValueControl.setValue(event.documentType?.id);
      sourceValueControl.setErrors(documentTypeIsInvalid ? { invalid: true } : null);
      sourceValueControl.markAsDirty();
      return;
    }

    // set source value to null for legacy templates
    sourceValueControl.setValue(null);

    const destination = destinations.find((x) => x.type === event.destinationType);
    if (destination) {
      destination.documentType = event.documentType?.id;
    }

    this.updateFileTypeSectionFormValue(formGroup, destinations, true, documentTypeIsInvalid);
  }

  onDocxFileNameChange(fileName: string): void {
    setTimeout(() => {
      this.docxFileName = fileName;
    }, 0);
  }

  getDynamicSections(): FormGroup[] {
    const toInclude = this.templateService.dynamicSectionsToInclude;
    return this.sectionsFormArray.controls.filter((section: FormGroup) => toInclude.includes(section.get('type')?.value)) as FormGroup[];
  }

  clearDynamicSections(): void {
    this.getDynamicSections().forEach((section) => {
      const sectionIndex = this.sectionsFormArray.controls.indexOf(section);
      if (sectionIndex >= 0) {
        this.sectionsFormArray.removeAt(sectionIndex);
      }
    });
  }

  getDynamicSectionIndex(sectionIndex: number): number {
    const dynamicSections = this.getDynamicSections();
    const targetSection = dynamicSections[sectionIndex];
    return this.sectionsFormArray.controls.indexOf(targetSection);
  }

  addDynamicSection(section: FormGroup): void {
    this.sectionsFormArray.push(section);
    this.updateOrder();
    this.form.markAsDirty();
  }

  removeDynamicSection(sectionIndex: number): void {
    const actualIndex = this.getDynamicSectionIndex(sectionIndex);
    if (actualIndex >= 0) {
      this.sectionsFormArray.removeAt(actualIndex);
      this.updateOrder();
      this.form.markAsDirty();
    }
  }

  reorderSections(previousIndex: number, currentIndex: number): void {
    if (previousIndex >= 0 && currentIndex >= 0) {
      const section = this.sectionsFormArray.at(previousIndex);
      this.sectionsFormArray.removeAt(previousIndex);
      this.sectionsFormArray.insert(currentIndex, section);
      this.updateOrder();
    }
  }

  ngOnDestroy(): void {
    this.utilsService.dialogOpen(false);
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  // Keyboard Shortcut - Use Esc to clear
  @HostListener('document:keydown.escape', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (!this.utilsService.escapableElementActive() && !this.editingTemplateName) {
      this.dialogRef.close();
    } else {
      this.utilsService.escapableElementActive(false);
    }
    if (this.editTemplateNameInput?.nativeElement.focus) {
      this.editingTemplateName = false;
      this.configuration.get('name').setValue(this.workflowConfig.name);
    }
  }

  private isDirectLinkEnabledForWorkflow(): boolean {
    if (this.workflowConfig.workflow === Workflow.DocumentMerge) {
      return this.templateDirectUrlDocMergeFeatureFlagEnabled();
    }

    return true;
  }

  private initSubscriptions(): void {
    this.subscriptions.add(
      this.configuration.get('entity').valueChanges.subscribe((entityValue) => {
        this.showUserRestrictions = this.namespace === Namespace.bullhorn && !!entityValue;
      }),
    );

    this.subscriptions.add(
      this.configuration.valueChanges.subscribe((_) => {
        this.tabsJustLoaded = false;
        this.updateValidationIconConfiguration();
      }),
    );

    this.subscriptions.add(
      this.signatureGroup.valueChanges.subscribe((_) => {
        this.tabsJustLoaded = false;
        this.updateValidationIconSignatureGroup();
      }),
    );

    this.subscriptions.add(
      this.signatureGroup.get('enabled').valueChanges.subscribe((enabled: boolean) => {
        if (enabled) {
          this.autoFillSignatureSection();
          this.setSignatureDestinationsForOutputFileType();
        }
      }),
    );

    this.subscriptions.add(
      this.actionsGroup.valueChanges.subscribe((_) => {
        this.tabsJustLoaded = false;
        this.updateValidationIconActionsGroup();
      }),
    );

    this.subscriptions.add(
      this.documentDestinationFormArray.valueChanges.subscribe((_) => {
        this.setDestinationsForOutputFileType(true);
        this.setSignatureDestinationsForOutputFileType(true);
      }),
    );

    this.subscriptions.add(
      this.dialogRef.backdropClick().subscribe((_) => {
        this.discardLocalChanges();
      }),
    );

    this.subscriptions.add(
      this.newTemplateName.valueChanges.subscribe((value) => {
        this.configuration.get('name').setValue(value);
      }),
    );

    this.subscriptions.add(
      this.configuration.get('useFileAttachmentAsTemplate').valueChanges.subscribe((checked) => {
        this.handleUseAttachmentAsTemplateChange(checked);
      }),
    );
  }

  private handleUseAttachmentAsTemplateChange(checked: boolean): void {
    const attachmentSection = this.findSectionFormGroup(SectionType.File);
    if (checked && attachmentSection) {
      attachmentSection.get('required').setValue(true);
    }
  }

  private getSectionOrderControls(): string[] {
    return this.sectionsFormArray.controls.map((section: FormGroup) => section.get('referenceId')?.value as string);
  }

  private updateOrder() {
    this.sectionsFormArray.controls.forEach((section: FormGroup, index: number) => {
      const orderControl = section.get('order');
      if (orderControl) {
        orderControl.setValue(index);
        const referenceId = section.get('referenceId')?.value as string;
        if (this.originalSectionOrder.indexOf(referenceId) === index) {
          orderControl.markAsPristine();
        } else {
          orderControl.markAsDirty();
        }
      }
    });
    console.debug('New section order:', this.getSectionOrderControls());
  }

  private updateShowUserRestrictions(): void {
    const entityValue = this.configuration?.get('entity')?.value;
    this.showUserRestrictions = this.namespace === Namespace.bullhorn && !!entityValue;
  }

  private updateValidationIconConfiguration(): void {
    setTimeout((_) => {
      if (this.configuration.valid) {
        this.validationIconConfiguration = 'check_circle';
      } else {
        this.validationIconConfiguration = 'error';
      }
    }, 300);
  }

  private updateValidationIconSignatureGroup(): void {
    setTimeout((_) => {
      if (this.signatureGroup.valid) {
        this.validationIconSignatureGroup = 'check_circle';
        this.tooltipText = '';
      } else {
        this.validationIconSignatureGroup = 'error';
        this.tooltipText = this.buildTooltipText();
      }
    }, 300);
  }

  private updateValidationIconActionsGroup(): void {
    setTimeout((_) => {
      if (this.actionsGroup.valid) {
        this.validationIconActionsGroup = 'check_circle';
        this.actionsTooltipText = '';
      } else {
        this.validationIconActionsGroup = 'error';
        this.actionsTooltipText = this.buildActionsTooltipText();
      }
    }, 300);
  }

  private buildActionsTooltipText(): string {
    if (!this.actionsFormArray) {
      return 'Information required';
    }

    for (const actionGroup of this.actionsFormArray.controls) {
      const actionsArray = actionGroup.get('properties')?.get('actions') as FormArray;
      if (!actionsArray?.controls) {
        continue;
      }

      for (const action of actionsArray.controls) {
        const actionControl = action.get('properties')?.get('fields')?.get('action');
        if (actionControl?.invalid && actionControl.hasError('maxlength')) {
          return 'Maximum characters reached in action';
        }
      }
    }

    return 'Information required';
  }

  private monitorTemplateFileGroup(): void {
    const isUseAttachmentAsTemplate: boolean = this.configuration.get('useFileAttachmentAsTemplate').value;
    const templateFormGroups = this.sectionsFormArray.controls.filter((fg: UntypedFormGroup) => {
      const type = fg.get('type').value;
      return type === SectionType.DocxTemplate || type === SectionType.HtmlDocxTemplate;
    });

    const atLeastOneRequired = () => templateFormGroups.some((g: UntypedFormArray) => g.get('required').value);
    const markFormGroupsAsValid = () => {
      templateFormGroups.forEach((fg: UntypedFormGroup) => {
        fg.get('sourceValue').setValue(fg.get('sourceValue').value || '');
      });
    };
    const markFormGroupsAsInvalid = () => {
      templateFormGroups.forEach((fg: UntypedFormGroup) => {
        fg.get('sourceValue').setValue(fg.get('sourceValue').value || null);
      });
    };

    if (isUseAttachmentAsTemplate) {
      markFormGroupsAsValid();
      this.templateFilesValid = true;
    } else {
      markFormGroupsAsInvalid();
      this.templateFilesValid = atLeastOneRequired();
    }

    templateFormGroups.forEach((fg: UntypedFormGroup) => {
      this.subscriptions.add(
        fg.get('required').valueChanges.subscribe({
          next: (required) => {
            this.templateFilesValid = required || atLeastOneRequired();
          },
        }),
      );
    });

    this.subscriptions.add(
      this.configuration.get('useFileAttachmentAsTemplate').valueChanges.subscribe({
        next: (useAttachment) => {
          // The sourceValue has a subscription in its own component to determine the required state.
          // Setting the value to empty string will mark the field as non-required.
          // Setting to null would bypass that logic and always set required to true.
          // But always check if the sourceValue is already set to avoid overwriting user input.
          if (useAttachment) {
            markFormGroupsAsValid();
          } else {
            markFormGroupsAsInvalid();
          }
        },
      }),
    );
  }

  private buildTooltipText(): string {
    const invalidFields: string[] = [];

    if (!this.signatureGroup.get('recipients').valid) {
      invalidFields.push('Recipients');
    }

    if (!this.signatureGroup.get('emailBody').valid) {
      invalidFields.push('Email Body');
    }

    if (!this.signatureGroup.get('emailSubject').valid) {
      invalidFields.push('Email Subject');
    }

    if (!this.signatureGroup.get('properties').get('brandId').valid) {
      invalidFields.push('Signature Brand');
    }

    if (!this.signatureGroup.get('properties').get('alternativeAccount').valid) {
      invalidFields.push('Signature Account');
    }

    if (!this.signatureGroup.get('properties').get('templateId').valid) {
      invalidFields.push('Template');
    }

    if (!this.signatureGroup.get('properties').get('documentPosition').valid) {
      invalidFields.push('Template Document Position');
    }

    return `Invalid Fields: ${invalidFields.join(', ')}`;
  }

  private autoFillSignatureSection(): void {
    if (!this.signatureGroup.get('enabled').value) {
      return;
    }
    if (!this.findSectionFormGroup(SectionType.SignatureOutputFileType)) {
      SectionUtils.addSection(this.sectionsFormArray, SectionType.SignatureOutputFileType);
    }
    this.signatureOutputFileTypeSectionFormGroup = this.findSectionFormGroup(SectionType.SignatureOutputFileType);
  }

  private autoFillSections(sectionTypes: SectionType[]): void {
    const present: string[] = [];

    this.sectionsFormArray.controls.forEach((fg: UntypedFormGroup) => {
      present.push(SectionUtils.sectionType(fg));
    });

    for (const sectionType of sectionTypes) {
      if (!present.find((sectionTypeValue) => sectionTypeValue === sectionType)) {
        SectionUtils.addSection(this.sectionsFormArray, sectionType);
      }
    }
  }

  private findSectionFormGroup(type: string): UntypedFormGroup {
    return this.sectionsFormArray.controls.find((control) => {
      const formGroup: UntypedFormGroup = control as UntypedFormGroup;
      return formGroup.get('type').value === type;
    }) as UntypedFormGroup;
  }

  private buildScriptingBoxForm(config?: IScriptingBoxConfig): UntypedFormGroup {
    return this.formBuilder.group({
      language: new UntypedFormControl(config?.language || 'js'),
      script: new UntypedFormControl(config?.script || ''),
      model: new UntypedFormControl(config?.model || {}),
      dependencies: new UntypedFormControl(config?.dependencies || []),
    });
  }

  private buildDocumentDestinationFormArray(): UntypedFormArray {
    const formArray = new UntypedFormArray([]);
    const destinationsConfigs = this.workflowConfig?.actionConfigs?.filter((x) => x.type === 'documentDestination');
    if (destinationsConfigs?.length > 0) {
      for (let i = 0; i < destinationsConfigs.length; i++) {
        formArray.push(
          new UntypedFormGroup({
            entityType: new UntypedFormControl(
              (
                destinationsConfigs[i].properties as {
                  entityType: string;
                  paths: string[];
                }
              ).entityType,
              Validators.required,
            ),
            paths: new UntypedFormControl(
              (
                destinationsConfigs[i].properties as {
                  entityType: string;
                  paths: string[];
                }
              ).paths ?? [],
              Validators.required,
            ),
          }),
        );
      }
    }
    return formArray;
  }

  private setDestinationsForOutputFileType(updateForm?: boolean): void {
    this.loadFileTypeDestinations(this.outputFileTypeSectionFormGroup).subscribe((d) => {
      this.destinationsForOutputFileType = d;
      if (updateForm) {
        this.updateFileTypeSectionFormValue(this.outputFileTypeSectionFormGroup, d, false);
      }
    });
  }

  private setSignatureDestinationsForOutputFileType(updateForm?: boolean): void {
    if (!this.signatureOutputFileTypeSectionFormGroup) {
      return;
    }
    this.loadFileTypeDestinations(this.signatureOutputFileTypeSectionFormGroup).subscribe((d) => {
      this.signatureDestinationsForOutputFileType = d;
      if (updateForm) {
        this.updateFileTypeSectionFormValue(this.signatureOutputFileTypeSectionFormGroup, d, false);
      }
    });
  }

  private loadFileTypeDestinations(formGroup: UntypedFormGroup): Observable<IDestinationsForOutputFileType[]> {
    if (!this.documentDestinationOptionsAllowed) {
      return EMPTY;
    }

    const destinations = this.documentDestinationFormArray.value;
    const destinationOptions: string[] = [];
    for (let i = 0; i < destinations.length; i++) {
      destinations[i].paths.map((x) => destinationOptions.push(this.documentDestinationService.getEntityTypeFromDestinationPath(x)));
    }

    const destinationEntityTypes: string[] = [...new Set(destinationOptions)];
    return this.documentDestinationService.documentDestinationMeta$.pipe(
      map((meta) => this.documentDestinationService.getDestinationsForOutputFileType(
        meta,
        destinationEntityTypes,
        formGroup.value
      ))
    );
  }

  private updateFileTypeSectionFormValue(
    formGroup: UntypedFormGroup,
    destinations: IDestinationsForOutputFileType[],
    markFormAsDirty: boolean,
    documentTypeIsInvalid?: boolean
  ): void {
    if (!destinations?.length) {
      return;
    }

    let destinationsMap = {};
    for (let i = 0; i < destinations.length; i++) {
      destinationsMap = {
        ...destinationsMap,
        ...destinations[i].entityTypes.reduce(
          (acc, curr) => ((acc[curr] = destinations[i].documentType), acc),
          {},
        ),
      };
    }

    const configControl = formGroup.get('config');
    // in case there are other config settings other than destinations we retain the value
    configControl.setValue({ ...formGroup.get('config').value, destinations: destinationsMap });

    if (markFormAsDirty) {
      configControl.setErrors(documentTypeIsInvalid ? { invalid: true } : null);
      configControl.markAsDirty();
    }
  }
}
